import '../styles/settings.css';
import { iconMarkup, type HudIcon } from '../icons.js';
import { setTooltip } from '../tooltips.js';
import { playSlab } from '../../audio/slab-sound.js';

/** A round carved seal holding a glyph, used on menu tablets and the settings tabs. */
export const seal = (name: HudIcon) => `<span class="menu-seal">${iconMarkup(name)}</span>`;

interface SettingBase { id: string; label: string; visible?: () => boolean; enabled?: () => boolean }
export interface RangeSetting extends SettingBase { kind: 'range'; min: number; max: number; step: number; get(): number; set(value: number): void; format?(value: number): string }
export interface ToggleSetting extends SettingBase { kind: 'toggle'; get(): boolean; set(value: boolean): void }
export interface ChoiceSetting extends SettingBase { kind: 'choice'; choices: readonly { value: string; label: string }[]; get(): string; set(value: string): void }
/** A button. Without `run` its behavior is bound by whoever owns it (fullscreen, invite…), found by its id. */
export interface ActionSetting extends SettingBase { kind: 'action'; run?(): void }
/** Anything the declarative kinds cannot say: a status line, a key list, the credits, a tool's own dials. */
export interface ContentSetting { kind: 'content'; id?: string; render(): HTMLElement | string; visible?: () => boolean }
/** A small engraved heading between groups of one tab. */
export interface HeadingSetting { kind: 'heading'; label: string; visible?: () => boolean }
export type Setting = RangeSetting | ToggleSetting | ChoiceSetting | ActionSetting | ContentSetting | HeadingSetting;

export interface SettingsTab {
  id: string; label: string; icon: HudIcon; settings: Setting[];
  visible?: () => boolean;
  /** Subscribes to outside changes (the mixer, the graphics options) so the tab's controls follow them. */
  watch?: (refresh: () => void) => void;
  /** The scene stays clear behind the modal while this tab is open, for dials judged on the picture. */
  seeThrough?: boolean;
}

export interface SettingsOptions {
  /** Shown under the tabs on every tab (the game's Close NimbleZ). */
  footer?: string;
  /** The gear button that opens the modal; `before` runs first (the game releases a captured cursor). */
  opener?: HTMLElement | null; before?: () => void;
}

const TAB_KEY = 'nimblez.settings.tab';
const readTab = () => { try { return localStorage.getItem(TAB_KEY); } catch { return null; } };
const saveTab = (id: string) => { try { localStorage.setItem(TAB_KEY, id); } catch { /* Opens on the first tab next time. */ } };

/**
 * The one settings modal of the game, the scenario viewer and the explorer: a stone slab in the middle of the screen with a rail of
 * sealed tabs. Each page lists the tabs it has; the controls are drawn from their declarations and kept in step with what they read.
 */
export class SettingsDialog {
  readonly element: HTMLDialogElement;
  private readonly rail: HTMLElement;
  private readonly refreshers: (() => void)[] = [];
  private readonly tabButtons = new Map<string, HTMLButtonElement>();
  private readonly panels = new Map<string, HTMLElement>();
  private readonly listeners = new Set<(tab: string) => void>();
  private tab: string;

  constructor(mount: HTMLElement, private readonly tabs: SettingsTab[], options: SettingsOptions = {}) {
    const dialog = this.element = document.createElement('dialog');
    dialog.id = 'settings'; dialog.className = 'settings'; dialog.setAttribute('aria-labelledby', 'settings-title');
    dialog.tabIndex = -1;
    dialog.innerHTML = `
      <div class="settings-header"><h2 id="settings-title">Settings</h2><button id="settings-close" class="settings-close" type="button">${iconMarkup('close')}</button></div>
      <nav class="settings-tabs" role="tablist" aria-orientation="vertical"></nav>
      <div class="settings-panels"></div>
      ${options.footer ? `<div class="settings-footer">${options.footer}</div>` : ''}`;
    const close = dialog.querySelector<HTMLElement>('#settings-close')!;
    setTooltip(close, 'Close', 'Close settings');
    this.rail = dialog.querySelector('.settings-tabs')!;
    const panels = dialog.querySelector('.settings-panels')!;
    for (const tab of tabs) {
      const button = document.createElement('button');
      Object.assign(button, { type: 'button', id: `settings-tab-${tab.id}`, className: 'settings-tab', innerHTML: `${seal(tab.icon)}<span>${tab.label}</span>` });
      button.setAttribute('role', 'tab'); button.setAttribute('aria-controls', `settings-panel-${tab.id}`);
      button.addEventListener('click', () => this.select(tab.id));
      this.rail.append(button); this.tabButtons.set(tab.id, button);
      const panel = document.createElement('section');
      Object.assign(panel, { id: `settings-panel-${tab.id}`, className: 'settings-panel' });
      panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', button.id);
      const refresh = this.fill(panel, tab.settings);
      this.refreshers.push(refresh); tab.watch?.(() => { if (this.element.open) refresh(); });
      panels.append(panel); this.panels.set(tab.id, panel);
    }
    this.rail.addEventListener('keydown', event => this.moveTab(event));
    mount.append(dialog);
    close.addEventListener('click', () => this.dismiss());
    // Escape slides the slab away like the close button instead of removing it at once.
    dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismiss(); });
    options.opener?.addEventListener('click', () => { options.before?.(); this.show(); });
    // Read on opening, not now: the page may still be setting up what the tabs read.
    this.tab = readTab() ?? tabs[0]?.id ?? '';
  }

  get open() { return this.element.open; }
  get current() { return this.tab; }
  onSelect(listener: (tab: string) => void) { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }

  /** Opens on `tab`, or where it was left. */
  show(tab?: string) {
    if (tab) this.tab = tab;
    this.refresh();
    if (this.element.open && !this.element.classList.contains('closing')) return;
    this.element.classList.remove('closing');
    // The slab itself takes focus on opening, so no control shows a focus ring until the keyboard moves.
    if (!this.element.open) { this.element.showModal(); this.element.focus(); }
    playSlab();
    for (const listener of this.listeners) listener(this.tab);
  }
  /** Lifts the slab away, then closes the dialog; `close` is the immediate version for callers that need it gone now. */
  dismiss() {
    const dialog = this.element;
    if (!dialog.open || dialog.classList.contains('closing')) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { this.close(); return; }
    dialog.classList.add('closing'); playSlab();
    const done = () => { clearTimeout(fallback); dialog.removeEventListener('animationend', onEnd); dialog.classList.remove('closing'); if (dialog.open) dialog.close(); };
    const onEnd = (event: AnimationEvent) => { if (event.target === dialog && event.animationName === 'settings-out') done(); };
    const fallback = setTimeout(done, 700);
    dialog.addEventListener('animationend', onEnd);
  }
  close() { this.element.classList.remove('closing'); if (this.element.open) this.element.close(); }
  /** Opens or closes on `tab`: a key that names a tab toggles it. */
  toggle(tab: string) { if (this.open && this.tab === tab) this.dismiss(); else this.show(tab); }

  select(id: string) {
    if (!this.tabButtons.has(id) || id === this.tab && this.panels.get(id)!.classList.contains('active')) return;
    this.tab = id; saveTab(id); this.refresh();
    for (const listener of this.listeners) listener(id);
  }

  /** Reads every control again and shows the tabs that apply now; the open tab falls back to the first one shown. */
  refresh() {
    const shown = this.tabs.filter(tab => tab.visible?.() ?? true);
    if (!shown.some(tab => tab.id === this.tab)) this.tab = shown[0]?.id ?? '';
    for (const tab of this.tabs) {
      const on = tab.id === this.tab, button = this.tabButtons.get(tab.id)!;
      button.hidden = !shown.includes(tab);
      button.setAttribute('aria-selected', String(on)); button.classList.toggle('active', on); button.tabIndex = on ? 0 : -1;
      this.panels.get(tab.id)!.classList.toggle('active', on);
    }
    // One tab needs no rail.
    this.element.classList.toggle('single', shown.length < 2);
    this.element.classList.toggle('see-through', !!shown.find(tab => tab.id === this.tab)?.seeThrough);
    for (const refresh of this.refreshers) refresh();
  }

  private moveTab(event: KeyboardEvent) {
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const shown = this.tabs.filter(tab => !this.tabButtons.get(tab.id)!.hidden), at = shown.findIndex(tab => tab.id === this.tab);
    const next = shown[(at + step + shown.length) % shown.length];
    if (next) { this.select(next.id); this.tabButtons.get(next.id)!.focus(); }
  }

  /** Draws a tab's settings into its panel; consecutive buttons share a row. Returns what puts the controls back in step. */
  private fill(panel: HTMLElement, settings: Setting[]) {
    const updates: (() => void)[] = [];
    let actions: HTMLElement | null = null;
    for (const setting of settings) {
      if (setting.kind !== 'action') actions = null;
      const { node, update } = this.control(setting);
      if (setting.kind === 'action') {
        if (!actions) { actions = document.createElement('div'); actions.className = 'settings-actions'; panel.append(actions); }
        actions.append(node);
      } else panel.append(node);
      // Only a declared `visible` is applied: other buttons (capture cursor, leave room) are shown and hidden by their owners.
      const visible = setting.visible;
      updates.push(() => {
        if (visible) node.hidden = !visible();
        update?.();
      });
    }
    return () => { for (const update of updates) update(); };
  }

  private control(setting: Setting): { node: HTMLElement; update?: () => void } {
    switch (setting.kind) {
      case 'heading': {
        const node = document.createElement('h3'); node.textContent = setting.label; return { node };
      }
      case 'content': {
        const made = setting.render();
        const node = typeof made === 'string' ? Object.assign(document.createElement('div'), { innerHTML: made }) : made;
        if (setting.id) node.id = setting.id;
        node.classList.add('settings-content');
        return { node };
      }
      case 'action': {
        const node = Object.assign(document.createElement('button'), { type: 'button', id: setting.id, className: 'secondary', textContent: setting.label });
        if (setting.run) node.addEventListener('click', () => setting.run!());
        const enabled = setting.enabled;
        return { node, update: enabled && (() => { node.disabled = !enabled(); }) };
      }
      case 'toggle': {
        const node = document.createElement('label'); node.className = 'switch';
        const input = Object.assign(document.createElement('input'), { type: 'checkbox', id: setting.id });
        input.addEventListener('change', () => setting.set(input.checked));
        node.append(input, setting.label);
        return { node, update: () => { input.checked = setting.get(); input.disabled = !(setting.enabled?.() ?? true); node.classList.toggle('disabled', input.disabled); } };
      }
      case 'choice': {
        const node = document.createElement('label'); node.className = 'setting-choice';
        const select = document.createElement('select'); select.id = setting.id;
        select.replaceChildren(...setting.choices.map(choice => new Option(choice.label, choice.value)));
        select.addEventListener('change', () => setting.set(select.value));
        node.append(Object.assign(document.createElement('span'), { textContent: setting.label }), select);
        return { node, update: () => { select.value = setting.get(); select.disabled = !(setting.enabled?.() ?? true); } };
      }
      case 'range': {
        const node = document.createElement('label'); node.className = 'setting-range';
        const head = Object.assign(document.createElement('span'), { className: 'setting-value', textContent: setting.label });
        const output = document.createElement('output'); output.htmlFor.add(setting.id); head.append(output);
        const input = Object.assign(document.createElement('input'), { type: 'range', id: setting.id, min: String(setting.min), max: String(setting.max), step: String(setting.step) });
        input.setAttribute('aria-label', setting.label);
        const show = (value: number) => {
          const text = setting.format?.(value) ?? String(value);
          output.textContent = text; input.setAttribute('aria-valuetext', text);
        };
        input.addEventListener('input', () => { const value = Number(input.value); setting.set(value); show(value); });
        node.append(head, input);
        return { node, update: () => { const value = setting.get(); input.value = String(value); show(value); input.disabled = !(setting.enabled?.() ?? true); } };
      }
    }
  }
}
